import React, { useState, useEffect } from 'react';
import { CustomButton } from '@/components/atomic';
import { ICONS } from '@/constants/constants';
import Image from 'next/image';

interface ResetLinkSentModalProps {
  email: string;
  onTryDifferentEmail: () => void;
  onResendRequest: () => void;
}

const ResetLinkSentModal: React.FC<ResetLinkSentModalProps> = ({
  email,
  onTryDifferentEmail,
  onResendRequest,
}) => {
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  const handleResend = () => {
    onResendRequest();
    setCountdown(60);
    setCanResend(false);
  };

  return (
    <div className="w-full text-center">
      {/* Success Icon and Title */}
      <div className="flex items-center justify-center mb-6">
        <Image 
          src={ICONS.greenTick} 
          alt="Success" 
          width={34} 
          height={34} 
          className="mr-3"
        />
        <h2 className="font-rakesly text-3xl font-bold text-gray-800 tracking-wide">
          RESET LINK SENT!
        </h2>
      </div>

      {/* Description */}
      <p className="text-gray-600 font-sans text-sm mb-6 leading-relaxed">
        We've sent a password reset link to {email}.<br />
        Please check your inbox and click the link to reset your password.
      </p>

      {/* Didn't receive email text */}
      <p className="text-gray-600 font-sans text-sm mb-4">
        Didn't receive the email?
      </p>

      {/* Countdown or Resend Button */}
      {!canResend ? (
        <p className="text-gray-600 font-sans text-sm mb-6">
          You can request another reset link in {countdown} seconds
        </p>
      ) : (
        <div className="mb-6">
          <CustomButton 
            onClick={handleResend}
            className="w-full cursor-pointer bg-gray-800 text-white py-2 px-4 rounded-lg font-sans font-medium hover:bg-gray-700 transition-all duration-200"
          >
            Resend request link
          </CustomButton>
        </div>
      )}

      {/* Try different email link */}
      <button 
        onClick={onTryDifferentEmail}
        className="text-gray-600 border-t  border-[#D1D5DB] w-full py-4 hover:text-gray-800 transition-colors font-work-sans text-md font-semibold"
      >
        Try a different email
      </button>
    </div>
  );
};

export default ResetLinkSentModal; 